/**
 * Settings > Portal rollout > New campaign (tracker item 81). The filters
 * (customer status, imported only, an optional list of customer ids, a
 * limit), the batch settings and the follow-up rules; a live preview (read
 * only, nothing is written) of how many customers will be invited and, for
 * every customer left out, why; then "Create draft" or "Create and start"
 * (which asks first and states how many emails will go out).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createAPIClient, IPortalRolloutApi } from '../../api';
import * as types from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { canManagePortalRollout, canManagePortalSettings } from '../../utils/permissions';
import { getErrorMessage } from '../../utils/apiError';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { RolloutBlockers } from './RolloutBlockers';
import { RolloutConfirm } from './RolloutConfirm';
import {
  CUSTOMER_STATUS_OPTIONS,
  CampaignForm,
  DEFAULT_CAMPAIGN_FORM,
  PORTAL_ROLLOUT_PATH,
  PREVIEW_SKIP_REASONS,
  campaignInput,
  durationText,
  plural,
  rolloutCampaignPath,
  rolloutErrorText,
  skipReasonLabel,
} from './portalRolloutUi';

/** How long the form must be still before the preview is asked again. */
export const PREVIEW_DEBOUNCE_MS = 400;

/** The reasons present in a preview: the known ones in the server's order, then any the UI does not know yet. */
function skipReasonCodes(preview: types.RolloutPreview): string[] {
  const known: string[] = [...PREVIEW_SKIP_REASONS];
  return [
    ...known.filter((code) => (preview.bySkipReason[code] ?? 0) > 0),
    ...Object.keys(preview.bySkipReason).filter((code) => !known.includes(code) && preview.bySkipReason[code] > 0),
  ];
}

export function PortalRolloutNewPage() {
  const { user } = useAuth();
  const api = useMemo<IPortalRolloutApi>(() => createAPIClient(), []);
  const navigate = useNavigate();
  const allowed = canManagePortalRollout(user);
  const isAdmin = canManagePortalSettings(user);
  const [form, setForm] = useState<CampaignForm>(DEFAULT_CAMPAIGN_FORM);
  const [readiness, setReadiness] = useState<types.RolloutReadiness | null>(null);
  const [preview, setPreview] = useState<types.RolloutPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmStart, setConfirmStart] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    if (!allowed) return undefined;
    let active = true;
    api.getPortalRolloutReadiness().then((data) => { if (active) setReadiness(data); }).catch(() => { /* the preview reports blockers too */ });
    return () => { active = false; };
  }, [api, allowed]);

  // The preview follows the filters and batch settings (not the name); only the latest answer is shown.
  const previewKey = JSON.stringify({ ...form, name: '' });
  useEffect(() => {
    if (!allowed) return undefined;
    const { input, error: problem } = campaignInput(form, false);
    setFormError(problem);
    if (!input) {
      setPreview(null);
      setPreviewError(null);
      setPreviewing(false);
      return undefined;
    }
    setPreviewing(true);
    const id = ++requestId.current;
    const timer = setTimeout(() => {
      api.previewPortalRollout(input)
        .then((data) => { if (id === requestId.current) { setPreview(data); setPreviewError(null); setPreviewing(false); } })
        .catch((cause) => { if (id === requestId.current) { setPreview(null); setPreviewError(getErrorMessage(cause, 'Could not preview the campaign')); setPreviewing(false); } });
    }, PREVIEW_DEBOUNCE_MS);
    return () => { clearTimeout(timer); };
    // previewKey carries every input that matters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, allowed, previewKey]);

  if (!allowed) return <p className="p-4 text-sm text-gray-600">Portal rollout is available to admins and office managers.</p>;

  const update = (patch: Partial<CampaignForm>) => { setConfirmStart(false); setForm((current) => ({ ...current, ...patch })); };
  const toggleStatus = (status: types.RolloutCustomerStatus) =>
    update({ statuses: form.statuses.includes(status) ? form.statuses.filter((s) => s !== status) : [...form.statuses, status] });

  const blockers = preview ? preview.blockers : readiness?.blockers ?? [];
  const previewFresh = !!preview && !previewing && !previewError && !formError;
  const nameProblem = form.name.trim().length < 1 || form.name.trim().length > 120;

  async function create(start: boolean) {
    const { input, error: problem } = campaignInput(form, true);
    if (!input) { setError(problem); setConfirmStart(false); return; }
    setBusy(true);
    setError(null);
    let created: types.RolloutCampaign;
    try {
      created = await api.createPortalRolloutCampaign(input);
    } catch (cause) {
      setError(rolloutErrorText(cause, 'Could not create the campaign'));
      setConfirmStart(false);
      setBusy(false);
      return;
    }
    let notice: string | null = null;
    if (start) {
      try {
        await api.startPortalRolloutCampaign(created.id);
      } catch (cause) {
        notice = `The campaign was created but not started. ${rolloutErrorText(cause, 'Could not start the campaign')}`;
      }
    }
    setBusy(false);
    navigate(rolloutCampaignPath(created.id), { state: { notice } });
  }

  function requestCreate(start: boolean) {
    if (formError) { setError(formError); return; }
    if (nameProblem) { setError('Name the campaign (up to 120 characters).'); return; }
    setError(null);
    if (start) setConfirmStart(true);
    else void create(false);
  }

  const gap = Number(form.batchGapMinutes);

  return (
    <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <p className="text-sm"><Link to={PORTAL_ROLLOUT_PATH} className="inline-block py-2 text-blue-700 underline">Portal rollout</Link></p>
      <h1 className="mt-1 text-2xl font-bold text-gray-900">New rollout campaign</h1>
      <p className="mt-1 text-sm text-gray-600">
        Choose who to invite. The preview below shows who would get an email and who would be left out, and nothing is sent or saved until you create the campaign.
      </p>

      {blockers.length > 0 && <div className="mt-4"><RolloutBlockers blockers={blockers} isAdmin={isAdmin} heading="You can create the campaign, but it cannot start yet" /></div>}

      <form className="mt-6 space-y-6 rounded-lg bg-white p-4 shadow sm:p-6" onSubmit={(e) => e.preventDefault()} noValidate aria-label="Campaign settings">
        <div>
          <label htmlFor="rollout-name" className={labelClass}>Campaign name</label>
          <input id="rollout-name" className={inputClass} maxLength={120} placeholder="For example: Welcome to the new portal" value={form.name} onChange={(e) => update({ name: e.target.value })} />
        </div>

        <fieldset>
          <legend className={labelClass}>Customers to invite</legend>
          <p className="mb-2 text-xs text-gray-500">Only customers with one of these statuses. Most companies invite active customers only.</p>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {CUSTOMER_STATUS_OPTIONS.map((option) => (
              <label key={option.value} className="flex min-h-[44px] items-center gap-2 text-sm text-gray-800 sm:min-h-0">
                <input type="checkbox" className="h-5 w-5" checked={form.statuses.includes(option.value)} onChange={() => toggleStatus(option.value)} />
                {option.label}
              </label>
            ))}
          </div>
          <label className="mt-3 flex min-h-[44px] items-center gap-2 text-sm text-gray-800 sm:min-h-0">
            <input type="checkbox" className="h-5 w-5" checked={form.importedOnly} onChange={(e) => update({ importedOnly: e.target.checked })} />
            Only customers added by a data import
          </label>
        </fieldset>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="rollout-limit" className={labelClass}>Invite at most (optional)</label>
            <input id="rollout-limit" className={inputClass} inputMode="numeric" placeholder="Everyone who qualifies" value={form.limit} onChange={(e) => update({ limit: e.target.value })} />
            <p className="mt-1 text-xs text-gray-500">The first customers who qualify, oldest first. Good for a small pilot.</p>
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="rollout-ids" className={labelClass}>Only these customers (optional)</label>
            <textarea id="rollout-ids" rows={2} className={inputClass} placeholder="Customer ids, one per line or separated by commas" value={form.customerIds} onChange={(e) => update({ customerIds: e.target.value })} />
            <p className="mt-1 text-xs text-gray-500">Leave blank to consider every customer with the statuses above.</p>
          </div>
        </div>

        <fieldset>
          <legend className={labelClass}>Sending speed</legend>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="rollout-batch" className={labelClass}>Emails per batch</label>
              <input id="rollout-batch" className={inputClass} inputMode="numeric" value={form.batchSize} onChange={(e) => update({ batchSize: e.target.value })} />
              <p className="mt-1 text-xs text-gray-500">1 to 200. A batch never holds back the company&apos;s other email: it is cut to what the sending limit allows.</p>
            </div>
            <div>
              <label htmlFor="rollout-gap" className={labelClass}>Minutes between batches</label>
              <input id="rollout-gap" className={inputClass} inputMode="numeric" value={form.batchGapMinutes} onChange={(e) => update({ batchGapMinutes: e.target.value })} />
              <p className="mt-1 text-xs text-gray-500">0 to 1440. Invitation links work for 7 days, so keep long campaigns moving.</p>
            </div>
          </div>
        </fieldset>

        <fieldset>
          <legend className={labelClass}>Follow-ups to customers who have not activated</legend>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="rollout-min-days" className={labelClass}>Days before a follow-up</label>
              <input id="rollout-min-days" className={inputClass} inputMode="numeric" value={form.minResendDays} onChange={(e) => update({ minResendDays: e.target.value })} />
              <p className="mt-1 text-xs text-gray-500">0 to 90. &quot;Resend to non-activated&quot; skips anyone emailed more recently.</p>
            </div>
            <div>
              <label htmlFor="rollout-max-resends" className={labelClass}>Most follow-ups per customer</label>
              <input id="rollout-max-resends" className={inputClass} inputMode="numeric" value={form.maxResends} onChange={(e) => update({ maxResends: e.target.value })} />
              <p className="mt-1 text-xs text-gray-500">0 to 10, after the first email.</p>
            </div>
          </div>
        </fieldset>
      </form>

      <section aria-labelledby="rollout-preview-heading" className="mt-6 rounded-lg bg-white p-4 shadow sm:p-6" data-testid="rollout-preview">
        <h2 id="rollout-preview-heading" className="text-lg font-semibold text-gray-900">Preview</h2>
        {formError && <p role="note" className="mt-2 text-sm text-red-700">{formError}</p>}
        {previewError && <p role="alert" className="mt-2 rounded bg-red-50 p-3 text-sm text-red-800">{previewError}</p>}
        {previewing && <p role="status" className="mt-2 text-sm text-gray-500">Checking who qualifies…</p>}
        {preview && !formError && (
          <div className={previewing ? 'opacity-60' : undefined}>
            <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded bg-green-50 p-3"><dt className="text-xs text-green-900">Will be invited</dt><dd className="text-2xl font-bold text-green-900" data-testid="preview-eligible">{preview.eligible.toLocaleString()}</dd></div>
              <div className="rounded bg-gray-50 p-3"><dt className="text-xs text-gray-600">Left out</dt><dd className="text-2xl font-bold text-gray-900" data-testid="preview-skipped">{preview.skipped.toLocaleString()}</dd></div>
              <div className="rounded bg-gray-50 p-3"><dt className="text-xs text-gray-600">Customers checked</dt><dd className="text-2xl font-bold text-gray-900">{preview.totalCandidates.toLocaleString()}</dd></div>
              <div className="rounded bg-gray-50 p-3"><dt className="text-xs text-gray-600">Batches</dt><dd className="text-2xl font-bold text-gray-900">{preview.batches.toLocaleString()}</dd></div>
            </dl>
            {preview.eligible > 0 && (
              <p className="mt-3 text-sm text-gray-700" data-testid="preview-estimate">
                {plural(preview.eligible, 'email')} in {plural(preview.batches, 'batch', 'batches')} of up to {form.batchSize}
                {preview.batches > 1 && Number.isFinite(gap) ? `, about ${durationText(preview.estimatedMinutes)} from start to finish` : ''}.
              </p>
            )}
            {preview.beyondLimit > 0 && <p className="mt-1 text-sm text-gray-600">{plural(preview.beyondLimit, 'more customer')} qualify but are beyond your limit.</p>}
            {preview.eligible === 0 && <p role="note" className="mt-3 rounded bg-amber-50 p-3 text-sm text-amber-900">Nobody qualifies with these settings, so there is nothing to send. Change the filters above.</p>}

            {preview.skipped > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-gray-800">Why customers are left out</h3>
                <ul className="mt-2 space-y-1 text-sm text-gray-700" aria-label="Skipped by reason">
                  {skipReasonCodes(preview)
                    .map((code) => (
                      <li key={code} data-reason={code}><span className="font-semibold">{preview.bySkipReason[code].toLocaleString()}</span> {skipReasonLabel(code).replace(/^./, (c) => c.toLowerCase())}</li>
                    ))}
                </ul>
                <details className="mt-3 rounded border border-gray-200">
                  <summary className="cursor-pointer p-3 text-sm font-medium text-gray-800">See the customers left out</summary>
                  <ul className="divide-y divide-gray-100 px-3 pb-2" aria-label="Customers left out">
                    {preview.skippedCustomers.map((s) => (
                      <li key={s.customerId} className="py-2 text-sm" data-testid="skipped-customer">
                        <Link to={`/customers/${encodeURIComponent(s.customerId)}`} className="font-medium text-blue-700 underline">{s.customerName}</Link>
                        {s.email && <span className="ml-2 break-all text-xs text-gray-500">{s.email}</span>}
                        <p className="text-xs text-gray-600">{skipReasonLabel(s.reason, s.reasonText)}</p>
                      </li>
                    ))}
                  </ul>
                  {preview.skippedTruncated && <p className="px-3 pb-3 text-xs text-gray-500">Showing the first {preview.skippedCustomers.length} only. The full list is in the campaign once it is created (and in its CSV export).</p>}
                </details>
              </div>
            )}

            {preview.sampleEligible.length > 0 && (
              <details className="mt-3 rounded border border-gray-200">
                <summary className="cursor-pointer p-3 text-sm font-medium text-gray-800">See who will be invited (first {preview.sampleEligible.length})</summary>
                <ul className="divide-y divide-gray-100 px-3 pb-2" aria-label="Customers to invite">
                  {preview.sampleEligible.map((s) => (
                    <li key={s.customerId} className="py-2 text-sm">{s.customerName}{s.email && <span className="ml-2 break-all text-xs text-gray-500">{s.email}</span>}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </section>

      {error && <p role="alert" className="mt-4 rounded bg-red-50 p-3 text-sm text-red-800">{error}</p>}

      {confirmStart && preview ? (
        <RolloutConfirm label="Confirm start" confirmLabel={`Yes, create and start (${plural(preview.eligible, 'email')})`} busy={busy} onConfirm={() => void create(true)} onCancel={() => setConfirmStart(false)}>
          <p>
            <strong>{plural(preview.eligible, 'email')}</strong> will go out in {plural(preview.batches, 'batch', 'batches')} of up to {form.batchSize}, one batch about every {form.batchGapMinutes} minutes.
          </p>
          <p>Each customer gets a portal login and a set-password link that works for 7 days. Customers who opted out are never emailed.</p>
        </RolloutConfirm>
      ) : (
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Link to={PORTAL_ROLLOUT_PATH} className={`${secondaryButtonClass} inline-flex items-center justify-center`}>Cancel</Link>
          <button type="button" className={secondaryButtonClass} disabled={busy || !previewFresh || preview?.eligible === 0} onClick={() => requestCreate(false)}>
            {busy ? 'Creating…' : 'Create draft'}
          </button>
          <button type="button" className={primaryButtonClass} disabled={busy || !previewFresh || preview?.eligible === 0 || blockers.length > 0} onClick={() => requestCreate(true)}>
            Create and start
          </button>
        </div>
      )}
      {blockers.length > 0 && <p className="mt-2 text-right text-xs text-gray-500">Starting is possible once the problems above are fixed.</p>}
    </main>
  );
}
